import {
  ArrowRightStartOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import { Fragment } from 'react';
import { NavLink } from 'react-router';
import { ServerFeatureFlag } from '@/components/feature-flag';
import { partnerSettingsTabs } from '@/components/settings/partners/menu';
import { useSignout } from '@/hooks/auth';
import { useSettingsSearchStore } from '@/stores/settings-search-store';
import { EDI_APP_URL } from '@/types/env';

const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
  `flex items-center justify-between gap-3 py-2 px-3 duration-300 hover:bg-zinc-800 hover:text-white ${isActive && 'bg-zinc-800 text-white'}`;

const buttonClasses =
  'flex w-full items-center justify-between gap-3 py-2 px-3 whitespace-nowrap duration-300 hover:bg-zinc-800 hover:text-white cursor-pointer';

type FeatureType = 'CRM' | 'EDI';

interface SettingsMenuTab {
  label: string;
  // タブ配下のサブタブ文言。
  subTabs?: string[];
  // 画面上の文言には出ないが検索でヒットさせたい別名(略語など)。
  keywords?: string[];
}

export interface SettingsMenuItem {
  label: string;
  to: string;
  // 配下のタブが to の兄弟パスに分かれている設定で、現在地の判定に使う共通パス。省略時は to で判定する。
  activePath?: string;
  featureFlag?: FeatureType;
  // 各設定配下のタブ・サブタブ文言。検索対象に含めるためのもので、遷移先は親メニュー(to)のまま。
  tabs?: SettingsMenuTab[];
}

// 取引先設定はタブUIと定義を共有する。
const partnerMenuTabs: SettingsMenuTab[] = partnerSettingsTabs.map((tab) => ({
  label: tab.label,
  subTabs: tab.subTabs?.map((subTab) => subTab.label),
}));

export const settingsMenuItems: SettingsMenuItem[] = [
  {
    label: '事業者設定',
    to: '/settings/organization',
    tabs: [{ label: '基本設定' }, { label: '粗利設定' }, { label: 'メニュー' }],
  },
  { label: '明細行項目設定', to: '/settings/item-display' },
  {
    label: '帳票設定',
    to: '/settings/document-settings',
    tabs: [{ label: '印影' }, { label: 'ロゴ' }, { label: '備考テンプレート' }],
  },
  { label: '流入経路設定', to: '/settings/lead-sources' },
  { label: '倉庫設定', to: '/settings/locations', tabs: [{ label: '倉庫' }, { label: '棚番' }] },
  {
    label: '見込客設定',
    to: '/settings/leads',
    featureFlag: 'CRM',
    tabs: [{ label: 'タグ' }, { label: 'カスタム項目' }, { label: '取引先名の表記ゆれ' }],
  },
  {
    label: '案件設定',
    to: '/settings/projects',
    tabs: [{ label: 'ステータス' }, { label: '受注確度' }, { label: 'カスタム項目' }],
  },
  {
    label: '見積書設定',
    to: '/settings/quotations',
    tabs: [{ label: 'カスタム項目' }, { label: 'テンプレート' }],
  },
  {
    label: '受注設定',
    to: '/settings/sales-orders',
    tabs: [
      { label: '基本設定' },
      { label: 'カスタム項目' },
      { label: '明細出力項目' },
      { label: '優先度' },
      { label: 'テンプレート', subTabs: ['納品書送付メール', '受注書テンプレート', '納品書テンプレート'] },
    ],
  },
  {
    label: '発注設定',
    to: '/settings/purchase-orders',
    activePath: '/settings/purchase-orders',
    tabs: [
      { label: 'ラベル' },
      { label: 'テンプレート', subTabs: ['発注書送付メール', '発注書テンプレート'] },
      { label: '出力項目' },
      { label: 'カスタム項目' },
    ],
  },
  {
    label: '生産設定',
    to: '/settings/manufacturing-orders',
    activePath: '/settings/manufacturing-orders',
    tabs: [{ label: 'カスタム項目' }],
  },
  {
    label: '請求設定',
    to: '/settings/invoices',
    tabs: [
      { label: '振込先' },
      { label: '決済方法' },
      { label: 'タグ' },
      { label: '出力項目' },
      { label: 'テンプレート', subTabs: ['請求書送付メール', '請求書テンプレート'] },
      { label: 'カスタム項目' },
    ],
  },
  { label: '在庫管理', to: '/settings/inventory', tabs: [{ label: '基本設定' }, { label: 'カスタム項目' }] },
  { label: '入出金設定', to: '/settings/payments', tabs: [{ label: '勘定科目' }] },
  { label: '外部連携設定', to: '/settings/external-integrations' },
  {
    label: 'EDI設定',
    to: '/settings/edi',
    featureFlag: 'EDI',
    tabs: [
      { label: '通知設定' },
      { label: '表示設定', keywords: ['料金', '価格', '単価'] },
      { label: 'お知らせ' },
    ],
  },
  {
    label: '商品設定',
    to: '/settings/products',
    tabs: [
      { label: '単位管理' },
      { label: '荷姿管理' },
      { label: '分類' },
      { label: '商品詳細', subTabs: ['カスタム項目', 'グループ'] },
      { label: '価格改定' },
    ],
  },
  { label: '取引先設定', to: '/settings/partners', tabs: partnerMenuTabs },
  { label: '部門管理', to: '/settings/members', tabs: [{ label: 'メンバー' }, { label: '部門' }] },
  { label: 'ユーザー設定', to: '/settings/user' },
  {
    label: 'セキュリティ',
    to: '/settings/security',
    activePath: '/settings/security',
    tabs: [
      { label: 'パスワード' },
      { label: '多要素認証', keywords: ['MFA', '2FA', '二段階認証'] },
      { label: '監査ログ' },
    ],
  },
  { label: 'API', to: '/settings/api' },
  { label: '倉庫アプリ', to: '/settings/warehouse-app' },
];

/** メニュー名・タブ名・サブタブ名・別名のいずれかがキーワードに部分一致するか。 */
export const matchesSettingsMenuKeyword = (item: SettingsMenuItem, keyword: string): boolean => {
  const normalizedKeyword = keyword.trim().toLowerCase();
  if (!normalizedKeyword) return true;

  const targets = [
    item.label,
    ...(item.tabs ?? []).flatMap((tab) => [tab.label, ...(tab.subTabs ?? []), ...(tab.keywords ?? [])]),
  ];
  return targets.some((target) => target.toLowerCase().includes(normalizedKeyword));
};

export const SettingsSidebar = () => {
  const { mutate: signout } = useSignout();
  const keyword = useSettingsSearchStore((state) => state.keyword);
  const setKeyword = useSettingsSearchStore((state) => state.setKeyword);

  const filteredItems = settingsMenuItems.filter((item) => matchesSettingsMenuKeyword(item, keyword));

  return (
    <aside className="w-full md:w-50 border-r border-neutral-200 bg-neutral-100 flex-shrink-0">
      <div className="p-2">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="設定を検索"
            className="w-full rounded border border-neutral-200 bg-white py-1.5 pl-8! pr-3"
          />
        </div>
      </div>
      <nav>
        {filteredItems.map((item) => {
          const link = (
            <NavLink to={item.to} className={navLinkClasses}>
              {item.label}
              <ChevronRightIcon className="w-5" />
            </NavLink>
          );

          return item.featureFlag ? (
            <ServerFeatureFlag key={item.to} featureType={item.featureFlag}>
              {link}
            </ServerFeatureFlag>
          ) : (
            <Fragment key={item.to}>{link}</Fragment>
          );
        })}
        {filteredItems.length === 0 && (
          <p className="px-3 py-2 text-sm text-neutral-400">該当する設定がありません</p>
        )}
      </nav>
      <ServerFeatureFlag featureType="EDI">
        <button type="button" onClick={() => window.open(EDI_APP_URL, '_blank')} className={buttonClasses}>
          アニマルEDI
          <ArrowTopRightOnSquareIcon className="w-5" />
        </button>
      </ServerFeatureFlag>
      <button
        type="button"
        onClick={() => window.open('https://manual.animal-office.jp/', '_blank')}
        className={buttonClasses}
      >
        ヘルプページ
        <ArrowTopRightOnSquareIcon className="w-5" />
      </button>
      <button type="button" onClick={() => signout()} className={buttonClasses}>
        ログアウト
        <ArrowRightStartOnRectangleIcon className="w-6" />
      </button>
    </aside>
  );
};
